<template>
  <div>
    <main class="bg-gray-100">
      <Title>{{ eventPage?.title }} - Buy Tickets</Title>
      <CartStepsOne v-if="eventPage?.cartStepsEnabled" />
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-4">
        <FallbackEvent
          v-if="
            !eventPage ||
              !eventPage.eventValid ||
              (eventPage.status && eventPage.status === 'Draft') ||
              (eventPage.status && eventPage.status === 'Off Sale')
          "
        />

        <!-- Loading Spinner -->
        <div v-if="eventPage.isLoading" class="mt-8">
          <div class="text-center">
            <div
              style="border-top-color: transparent"
              class="mx-auto w-16 h-16 border-4 border-black-400 border-solid rounded-full animate-spin"
            />
            <h3 class="mt-2 text-sm font-medium text-gray-900">
              Loading Tickets
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              Checking for available items...
            </p>
            <div class="mt-6" />
          </div>
        </div>

        <div
          v-if="
            eventPage &&
              eventPage.eventValid &&
              !eventHasEnded &&
              eventPage.status !== 'Draft'
          "
          class="mx-auto px-0 pb-8 mt-4"
        >
          <form
            v-if="eventPage.status === 'On Sale' || !eventPage.status"
            class="mt-0"
            @submit.prevent
          >
            <section aria-labelledby="cart-heading">
              <!-- EVENT HEADER -->
              <div
                id="ticket-select-event-header"
                class="px-2 pb-4 pt-4 text-xl font-semibold text-black-500 flex flex-auto"
              >
                <div class="flex-grow">
                  <h4 class="text-sm font-light">
                    Buy Tickets for
                  </h4>
                  <h3>{{ eventPage.title }}</h3>

                  <h4 class="text-base font-light">
                    {{ $dayjs(eventPage.startDate).format("ddd D MMM, YYYY") }}
                    <span
                      v-if="
                        !$dayjs(eventPage.startDate).isSame(eventPage.endDate)
                      "
                      class=""
                    >
                      -
                      {{ $dayjs(eventPage.endDate).format("ddd D MMM, YYYY") }}
                    </span>
                  </h4>
                  <div v-if="eventPage?.details" class="mt-2">
                    <NuxtLink
                      :to="`/buy/tickets-${route?.params?.tenant}/event-${route?.params?.eventSlug}/details`"
                      target="_blank"
                      class="text-sm md:text-base font-light text-blue-700 hover:underline cursor-pointer"
                    >
                      More about this event
                    </NuxtLink>
                  </div>
                </div>
                <div class="flex-shrink">
                  <CartMoreButton />
                </div>
              </div>
              <h2 id="cart-heading" class="sr-only">
                Select your tickets
              </h2>
              <!-- EVENT TICKET WRAPPER -->
              <div id="shopping-wrapper">
                <div
                  v-if="
                    !eventPage.ticketTypeCategories.length ||
                      ticketTypesOnSale < 1
                  "
                >
                  <div class="rounded-md bg-yellow-50 p-4">
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <ExclamationCircleIcon
                          class="h-5 w-5 text-yellow-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div class="ml-3">
                        <h3 class="text-sm font-medium text-yellow-800">
                          No tickets available
                        </h3>
                        <div class="mt-2 text-sm text-yellow-700">
                          <p>
                            Sorry, no tickets are available for this event at
                            the moment.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-for="category in eventPage.ticketTypeCategories"
                  :key="category.id"
                >
                  <div
                    v-if="
                      eventPage.ticketTypesByDisplayCategory(category.id).length
                    "
                    :id="`ticketcategory-${category.id}`"
                    class="flex flex-col my-6 sm:rounded-lg shadow bg-white"
                  >
                    <!-- CATEGORY HEADING -->
                    <div
                      class="flex px-4 py-4 text-lg font-semibold text-black-900"
                    >
                      <div class="flex-shrink pr-2">
                        <TicketIcon class="h-6 w-6 mt-1" aria-hidden="true" />
                      </div>
                      <div class="flex-grow">
                        <h3>{{ category.title }}</h3>
                        <p class="text-sm font-medium">
                          {{ category.description }}
                        </p>

                        <code
                          v-if="devView"
                          class="block whitespace-pre text-sm overflow-x-scroll"
                        >{{ category.id }}</code>
                      </div>
                    </div>

                    <!-- TICKET TYPES-->
                    <ul
                      v-if="eventPage.ticketTypes.length > 0"
                      role="list"
                      class="border-t border-b border-gray-200 divide-y divide-gray-200"
                    >
                      <li
                        v-for="(
                          product, productIdx
                        ) in eventPage.ticketTypesByDisplayCategory(
                          category.id
                        )"
                        :key="product.id"
                        class="flex py-4 px-4 sm:py-4 hover:bg-gray-50"
                        :class="
                          !product.maxQty || product.soldOut
                            ? 'bg-gray-50 hover:bg-gray-100 shadow-inner opacity-90 hover:opacity-100 border-dashed border-red-200 border-l-2 border-r-2'
                            : ''
                        "
                      >
                        <div class="flex-1 flex flex-col justify-between">
                          <div>
                            <div class="flex justify-between">
                              <!-- ON SALE TICKET DETAILS -->
                              <div class="pr-0">
                                <div class="flex flex-wrap">
                                  <!-- PRODUCT TITLE -->
                                  <h3
                                    class="flex flex-nowrap min-w-64 text-md text-gray-700 space-x-2"
                                  >
                                    <span class="mr-2">{{ product.name }}</span>
                                  </h3>
                                  <div
                                    v-if="!product.maxQty || product.soldOut"
                                    class="pr-0"
                                  >
                                    <!-- SOLD OUT CHIP -->
                                    <span
                                      class="bg-red-100 text-red-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded border border-red-400"
                                    >SOLD OUT</span>
                                  </div>
                                </div>
                                <div class="flex flex-col sm:flex-row">
                                  <div>
                                    <!-- PRICE -->
                                    <span class="mt-1 text-md text-black-500">{{
                                      $displayCurrency(product.price)
                                    }}</span>
                                    <!-- FEE -->
                                    <span
                                      v-if="product.fee"
                                      class="mt-1 ml-2 text-sm text-gray-400"
                                    >+
                                      {{ $displayCurrency(product.fee) }} fee</span>
                                  </div>
                                  <!-- BADGES -->
                                  <div class="inline-block mt-1 md:ml-2">
                                    <div class="grid grid-cols-12 sm:flex items-center gap-2">
                                      <div
                                        v-for="(badge, i) in product.badges"
                                        :key="i"
                                        class="col-span-12"
                                      >
                                        <TicketBadge :badge="badge" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  class="mt-1 text-xs text-gray-700 max-w-sm line-clamp-2 hover:line-clamp-none"
                                >
                                  {{ product.description }}
                                </div>
                                <div
                                  v-if="showMoreProducts.includes(product.id)"
                                  class="mt-1 flex text-sm text-gray-700 space-x-2"
                                >
                                  {{ product.description }}
                                </div>

                                <!-- DEV VIEW -->
                                <div
                                  v-if="devView"
                                  class="mt-1 text-gray-700 text-sm overflow-x-scroll font-mono bg-indigo-200"
                                >
                                  <div>
                                    ticketTypeId: {{ product.ticketTypeId }}
                                  </div>
                                  <div>
                                    displayCategory:
                                    {{ product.displayCategory }}
                                  </div>
                                </div>
                              </div>

                              <!-- BUTTONS / QUANTITY -->
                              <div class="items-end opacity-100">
                                <div v-if="!product.maxQty || product.soldOut">
                                  <!-- NOTIFY ME BUTTON -->
                                  <CartNotifyMeModal
                                    :product="product"
                                    :tenant-id="route.params.tenantId"
                                  />
                                </div>
                                <label
                                  :for="`quantity-${productIdx}`"
                                  class="sr-only"
                                >Quantity, {{ product.name }}</label>
                                <select
                                  v-if="!product.soldOut"
                                  :id="`quantity-${productIdx}`"
                                  v-model.number="
                                    s[((s[0] = getItemQty(product.id)), 0)]
                                  "
                                  :name="`quantity-${productIdx}`"
                                  class="block max-w-full rounded-md border border-gray-300 py-1.5 text-base leading-5 font-medium text-gray-700 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  @change="onChangeItemQty($event, product)"
                                >
                                  <option value="0">
                                    {{ `0` }}
                                  </option>
                                  <option
                                    v-for="i in product.maxQty"
                                    :value="i"
                                  >
                                    {{ `${i}` }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <!-- SOLD OUT ALERT -->

                            <div
                              v-if="!product.maxQty || product.soldOut"
                              class="flex p-4 mt-2 text-sm text-yellow-800 border border-yellow-300 rounded-lg bg-yellow-50"
                              role="alert"
                            >
                              <Icon
                                name="mdi:bell-ring"
                                class="flex-shrink-0 inline w-5 h-5 mr-3"
                              />
                              <span class="sr-only">Sold Out, register for notifications when more
                                tickets are available</span>
                              <div>
                                <span class="font-medium">Don't miss out!</span>
                                Register to be notified & get priority access to
                                resale tickets when they become available.
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <!-- Order summary -->
            <section
              aria-labelledby="summary-heading"
              class="mt-10 sm:ml-16 sm:pl-6 sm:mr-16"
            >
              <div class="bg-gray-50 rounded-lg px-4 py-6 sm:p-6 lg:p-8">
                <h2 id="summary-heading" class="sr-only">
                  Order summary
                </h2>

                <div class="flow-root">
                  <dl class="-my-4 text-sm divide-y divide-gray-200">
                    <div class="py-4 flex items-center justify-between">
                      <dt class="text-gray-600">
                        Total Tickets
                      </dt>
                      <dd class="font-medium text-gray-900">
                        {{ cart.totalTickets }}
                      </dd>
                    </div>
                    <div class="py-4 flex items-center justify-between">
                      <dt class="text-gray-600">
                        Subtotal
                      </dt>
                      <dd class="font-medium text-gray-900">
                        {{ $displayCurrency(cart.totalCartSubtotal) }}
                      </dd>
                    </div>
                    <div class="py-4 flex items-center justify-between">
                      <dt class="text-gray-600">
                        Fees
                      </dt>
                      <dd class="font-medium text-gray-900">
                        {{ $displayCurrency(cart.totalCartFees) }}
                      </dd>
                    </div>
                    <div class="py-4 flex items-center justify-between">
                      <dt class="text-base font-medium text-gray-900">
                        Total
                      </dt>
                      <dd class="text-base font-medium text-gray-900">
                        {{ $displayCurrency(cart.totalCartValue) }}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              <!-- Alert -->
              <div
                v-if="cart.preCartAlert"
                class="bg-red-50 border-l-4 mt-8 border-red-400 p-4"
              >
                <div class="flex">
                  <div class="flex-shrink-0">
                    <ExclamationCircleIcon
                      class="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-yellow-700">
                      {{ cart.preCartAlert }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-10">
                <button
                  :disabled="!cart.hasItems"
                  class="w-full bg-indigo-600 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-indigo-500 disabled:opacity-50"
                  @click="onProceedToCheckout"
                >
                  Continue
                </button>
              </div>
            </section>
          </form>
          <EventWaitlistSignupForm
            v-if="eventPage.status === 'Waitlist'"
            :id="eventPage.id"
            :title="eventPage.title"
            :slug="eventPage.slug"
          />
        </div>
        <div
          v-if="eventPage && eventPage.eventValid && eventHasEnded"
          class="mx-auto px-0 pb-8 mt-4"
        >
          <h1 class="font-semibold text-xl">
            {{ eventPage.title }} has ended.
          </h1>
          <h2 class="mt-1">
            Check out our upcoming events
            <span>
              <NuxtLink
                class="text-blue-700 hover:underline cursor-pointer font-semibold"
                :to="`/buy/tickets-${route?.params?.tenant}`"
              >
                here
              </NuxtLink>
            </span>
          </h2>
        </div>
      </div>

      <CartPoweredByWetixDivider v-if="route.query.embed" />

      <!-- Features Section -->
      <!-- <CartFeaturesRow v-if="eventPage && eventPage.cartTheme" /> -->
    </main>
  </div>
</template>

<script setup lang="ts">
import { ExclamationCircleIcon } from '@heroicons/vue/24/solid'
import { TicketIcon } from '@heroicons/vue/24/outline'
import { useCartStore } from '~/stores/cart'
import { useEventPages } from '~/stores/eventpages'

// Page meta
definePageMeta({
  layout: 'order'
})

const cart = useCartStore() // Import cart Pinia store
const eventPage = useEventPages() // Import eventPages Pinia store
// const tenantTheme = useTenantTheme() // Tenant Theme composable
const { $dayjs } = useNuxtApp() // dayJS composable
const route = useRoute() // Router
const config = useRuntimeConfig() // Get runtime config
const { $displayCurrency } = useNuxtApp() // Currency display helper
const $ga = useGoogleAnalytics() // Google Analytics composable

// On page load here, reset the state until we debug cart cartRecovery
cart.$reset()

// Add tenant ID to analytics user properties
$ga.setUserProperties({
  tenantId: route?.params?.tenant
})

// Fetch event page config from back-end (proxies to firestore) and set in store
try {
  eventPage.$patch({ isLoading: true })
  const { data: eventSpec } = (await useFetch(
    `${config.public.WETIX_SERVICE_API_BASE_URL}/v1/eventpages/${route.params.tenant}/${route.params.eventSlug}/details/`
  )) as any
  eventPage.$patch({ ...eventSpec.value, isLoading: false })
  cart.$patch({
    depositSchemeSettings: eventSpec.value.depositSchemeSettings,
    depositSchemeEnabled: eventSpec.value.depositSchemeEnabled
  })
} catch (error) {
  console.error(
    `Failed to fetch eventSpec for tenant ${route.params.tenant} and eventSlug ${route.params.eventSlug}`,
    error
  )
  console.error(error)
  eventPage.$patch({ isLoading: false, eventValid: false })
}

// Enable dev view if query parameters include dev
const devView = ref(false)
if (route.query.dev) {
  devView.value = true
}

// Item quantities hack - enables v-bind with select fields to work
const s = []
function getItemQty (idx) {
  if (!cart || !cart.cartItemQuantities) {
    return 0
  }
  return cart.cartItemQuantities[idx] ? cart.cartItemQuantities[idx] : 0
}

// If URL params contain a cartSessionId and local state doesn't have one, recover the cart state from the back-end
if (route.query?.cartSessionId && !cart.cartSessionId) {
  console.log(
    'cartSessionId found in query, restoring cart state from back-end'
  )
  // await recoverCartState()
  const { data: recoveredCartState } = (await useFetch(
    `${config.public.WETIX_SERVICE_API_BASE_URL}/v1/cart/${route.params.tenant}/session/${route.query.cartSessionId}/recover`
  )) as any
  console.log('Recovered cart state: ', recoveredCartState.value)
  if (!recoveredCartState.value) {
    cart.$reset()
    console.error('Failed to recover cart state')
  } else {
    console.log('Recovered cart state')
    if (recoveredCartState.value && recoveredCartState.value.recoverable) {
      const { cartState, cartStatePatch } = recoveredCartState.value
      // First restore the original cart state
      cart.$patch({
        ...cartState,
        recovered: true,
        showCartRecoveryModal: false,
        paymentSessionInitiated: false
      })
      // Then patch the state with anything the back-end tells us to.
      cart.$patch({ ...cartStatePatch })
    } else {
      const { cartStatePatch } = recoveredCartState.value
      console.error(
        `Unable to recover cart state: ${recoveredCartState.value.message} `,
        { ...recoveredCartState.value }
      )
      cart.$patch({ ...cartStatePatch })
    }
  }
}

// Change item quantities
const onChangeItemQty = (event, item) => {
  const qty = parseInt(event.target.value)
  // console.log(`New item qty: ${qty}`)
  if (qty === 0) {
    // console.log(`Removing item from cart..`)
    cart.removeItemFromCart(item)
  } else {
    cart.addItemToCart(item, qty)
  }
}

// Begin a checkout session when continue is clicked.
// TODO: [WTX-28] Implement stock reservation functionality to reserve tickets for 15mins when entering checkout process.
const onProceedToCheckout = async () => {
  // Check to see if products have been selected!
  if (cart.totalItemSkus === 0) {
    console.log('No products selected!')
    return cart.$patch({
      preCartAlert: 'Please select at least one product to continue.'
    })
  }

  // Create a cart session if we don't have one
  if (!cart.cartSessionId) {
    const { data: newCartSession } = (await useFetch(
      `${config.public.WETIX_SERVICE_API_BASE_URL}/v1/cart/${route.params.tenant}/session/create`,
      {
        method: 'POST',
        headers: useRequestHeaders(['cookie']),
        body: {
          tenantId: route.params.tenant,
          eventSlug: route.params.eventSlug
          // windowLocation: `${window.location}`,
        }
      }
    )) as any

    // Patch local cart state
    cart.$patch({
      cartSessionId: newCartSession.value.cartSessionId
    })
  }

  // Dynamic checkout path
  const checkoutRoute = `/buy/tickets-${route.params.tenant}/event-${route.params.eventSlug}/checkout`

  // Call proceedToCheckout in Pinia
  await cart.proceedToCheckout(checkoutRoute)
}

// Show more/less toggle
const showMoreProducts = ref([])
const toggleShowMoreProducts = (item) => {
  if (showMoreProducts.value.includes(item)) {
    showMoreProducts.value = showMoreProducts.value.filter(i => i !== item)
  } else {
    showMoreProducts.value.push(item)
  }
}

// Computed property which takes the eventPage endDate and checks if the date is in the past
const eventHasEnded = computed(() => {
  if (!eventPage || !eventPage.endDate) {
    return false
  }
  return $dayjs(eventPage.endDate).isBefore($dayjs())
})

// Computed property which returns the number of ticket types on sale
const ticketTypesOnSale = computed(() => {
  if (!eventPage || !eventPage.ticketTypeCategories) {
    return 0
  }
  let ticketTypes = 0
  eventPage.ticketTypeCategories.forEach((c) => {
    ticketTypes =
      ticketTypes + eventPage.ticketTypesByDisplayCategory(c.id).length
  })
  return ticketTypes
})
</script>
